<template>
  <!-- eslint-disable max-len -->
  <edit-page
    ref="editPage"
    name="Registration Link"
    model-name="registrationLink"
    param="registrationLinkId"
    page-prefix="/blade/registration-links"
    :create="createRegistrationLink"
    :fetch="getRegistrationLink"
    :fetch-types="getUserTypes"
    :update="updateRegistrationLink"
    :remove="deleteRegistrationLink"
    :stub="registrationLink"
    :validator="validator"
    :utils="utils"
  >
    <template
      slot-scope="{ model: registrationLink, validator, utils: { unused } }"
    >
      <template
        v-if="
          !creating && (registrationLink.link && registrationLink.link.length > 0 || registrationLink.isCustomLink)
        "
      >
        <text-input
          :disabled="true"
          label
          :value="getUrl(registrationLink)"
          copyable
          no-count
        >
          Registration Link
        </text-input>
        <checkbox-input
          v-model="registrationLink.isCustomLink"
          :disabled="!canEditCustomLink"
        >
          Use Custom Link
        </checkbox-input>

        <button v-if="!registrationLink.isCustomLink" class="btn" @click.prevent="registrationLink.link = null">
          Reset Link (will regenerate on saving)
        </button>

        <text-input v-if="canEditCustomLink && registrationLink.isCustomLink"
          v-model.trim="registrationLink.link"
          label
          required
          no-count
          :disabled="!canEditCustomLink"
        >Custom Registration Link</text-input>
      </template>

      <!-- metadata -->
      <text-input
        label
        v-model.trim="registrationLink.name"
        :validator="validator.name"
        required
        autofocus
      >
        Name
      </text-input>

      <checkbox-input
        v-model="registrationLink.uniqueIdSignin"
        :disabled="!canManageGames"
      >
        Use Unique ID Sign In
      </checkbox-input>
      <checkbox-input
        v-if="registrationLink.uniqueIdSignin"
        v-model="registrationLink.uniqueIdSigninAutoRegister"
        :disabled="!canManageGames"
      >
        Automatically Create Unique ID Accounts
      </checkbox-input>
      <checkbox-input
        v-if="registrationLink.uniqueIdSigninAutoRegister"
        v-model="registrationLink.uniqueIdSigninAutoGenerateCode"
        :disabled="!canManageGames"
      >
        Automatically Generate Unique ID (if none is provided)
      </checkbox-input>

      <template v-if="registrationLink.uniqueIdSignin && registrationLink.uniqueIdSigninAutoRegister">
        <!-- override autogen first/last name (overrideAutogenFirstName && *LastName) -->
        <text-input v-model.trim="registrationLink.overrideAutogenFirstName" label>Override Autogenerated First Name</text-input>
        <text-input v-model.trim="registrationLink.overrideAutogenLastName" label>Override Autogenerated Last Name</text-input>
      </template>

      <collapsible-section
        name="IP Rate Limiting Settings"
        :buttonStyle="{
          'font-size': '120%'
        }"
      >
        <div>
          The following settings allow you to limit the number of sign ups that
          can be made from a single IP address within a given time window. This
          is intended to prevent abuse of the registration link, particularly
          when anonymous registrations are enabled.  Note that, due to horizontal
          scaling, this is not an exact limit.

          <br/>
          <br/>
          <i>Note: If either setting is empty or 0, rate limiting is disabled.</i>
          <!-- Display for rate limit.  Displays "rate limit enabled: X sign ups allowed every {Y < 60 ? (Y seconds) : (Y / 60 minutes)}" -->
          <template
            v-if="
              registrationLink.ipBasedRateLimitCount > 0 &&
              registrationLink.ipBasedRateLimitWindowSeconds > 0
            "
          >
            <div class="rate-limit rate-limit-enabled">
              Rate Limit Enabled:
              {{ registrationLink.ipBasedRateLimitCount }} sign ups allowed
              every
              {{ rateLimitDisplayPeriod(registrationLink) }}
            </div>
          </template>
          <template v-else>
            <div class="rate-limit rate-limit-disabled">
              Rate Limit Disabled
            </div>
          </template>
        </div>

        <number-input
          label
          v-model.number="registrationLink.ipBasedRateLimitCount"
          :validator="validator.rateLimitUserCount"
          required
        >
          Rate Limit User Count
        </number-input>
        <number-input
          label
          v-model.number="registrationLink.ipBasedRateLimitWindowSeconds"
          :validator="validator.rateLimitWindowSeconds"
          required
        >
          Rate Limit Window (seconds)
        </number-input>
      </collapsible-section>

      <!-- Next Header -->
      <template v-if="registrationLink.uniqueIdSignin">
        <text-input
          label
          v-model.trim="registrationLink.uniqueIdLoginHeader"
        >
          Login Header Text
        </text-input>
        <text-input
          label
          v-model.trim="registrationLink.uniqueIdLoginPlaceholder"
        >
          Login Code Text
        </text-input>
      </template>
      <text-input
        v-if="
          registrationLink.uniqueIdSignin &&
          registrationLink.uniqueIdSigninAutoRegister
        "
        label
        v-model.trim="registrationLink.qualtricsRedirectURL"
      >
        Centiment Redirect URL (Game Complete URL)
      </text-input>
      <text-input
        v-if="
          registrationLink.uniqueIdSignin &&
          registrationLink.uniqueIdSigninAutoRegister
        "
        label
        v-model.trim="registrationLink.qualtricsExitURL"
      >
        Centiment Exit URL (I no longer want to take this survey URL)
      </text-input>

      <checkbox-input
        v-if="
          registrationLink.uniqueIdSignin &&
          registrationLink.uniqueIdSigninAutoRegister
        "
        v-model="registrationLink.disableCancelSurveyButton"
      >
        Disable Cancel Survey Button
      </checkbox-input>
      <text-input
        v-if="
          registrationLink.uniqueIdSignin &&
          registrationLink.uniqueIdSigninAutoRegister &&
          !registrationLink.disableCancelSurveyButton
        "
        label
        v-model.trim="registrationLink.cancelSurveyButtonText"
      >
        Cancel Survey Button Text (replaces "I no longer want to take this survey" text)
      </text-input>

      <checkbox-input
        v-if="!registrationLink.uniqueIdSignin"
        v-model="registrationLink.skipEmailVerification"
      >
        Skip Email Verification
      </checkbox-input>
      <checkbox-input v-else :value="true" disabled>
        Skip Email Verification (automatically enabled when using unique ID sign in)
      </checkbox-input>

      <asset-select-input
        label
        v-model="registrationLink.customEulaUrl"
        starting-path="documents/eula"
        filetypeFilter=".html"
      >Custom EULA (if blank, will use built-in EULA)</asset-select-input>
      <text-input
        label
        v-model.trim="registrationLink.emailDomain"
        :validator="validator.emailDomain"
        max-length
        no-count
      >
        Associated Email Domain (all {{ studentEmployeeStringLowerPlural }} required to join the {{ classTypeName }} using an email with this domain; supports wildcards)
      </text-input>

      <checkbox-input v-model="registrationLink.enableRegistrationNotifications">
        Enable Account Registration Notifications
      </checkbox-input>

      <template v-if="registrationLink.enableRegistrationNotifications">
        <text-input
          @input="emailListFlip = !emailListFlip"
          label
          v-model.trim="registrationLink.registrationEmailList"
          >Registration Notification Recipients</text-input
        >
        <div class="email-list-recipients">
          Notifications will be sent to {{ emailRecipientList }}
        </div>
      </template>
      <select-input
        label
        v-model="registrationLink.signInConfig"
        :options="signInConfigs"
        use="_id"
        :display="
          (e) => {
            return `${e.name}`;
          }
        "
        :to="
          (id) => {
            return `/blade/domainbrandings/${id}/edit`;
          }
        "
      >
        Domain Branding
      </select-input>

      <checkbox-input v-model="registrationLink.expires">
        Set A Date When Registration Link Expires
      </checkbox-input>
      <template v-if="registrationLink.expires">
        <h6>
          Expiration Date (cannot use this link after this time)
        </h6>
        <datetime
          v-model="registrationLink.expirationDate"
          type="datetime"
          :id="`dtpick-re`"
          use12-hour
          value-zone="UTC"
          zone="local"
        >
        </datetime>
      </template>

      <collapsible-section
        name="Coupons and Purchaseable Content"
        :buttonStyle="{
          'font-size': '120%'
        }"
      >
        <array-input :items="registrationLink.catalog" style="margin-top: 10px;">
          <h5 slot="title">Content Catalog (gamulation sets and prices)</h5>

          <template slot="item" slot-scope="{ item: listing, index: i }">
            <div
              class="form-row"
              style="
                gap: 16px;
                margin-bottom: 8px;
                padding-bottom: 6px;
                border-bottom: 1px solid darkgrey;
              "
            >
              <select-input
                v-model="listing.gameSet"
                :options="gameSetList"
                use="_id"
                display="name"
                required
                autofocus
                :remove="() => { registrationLink.catalog.splice(i, 1); }"
                :to="(id) => { return `/blade/gamesets/${id}/edit`; }"
              >
                Gamulation Set
              </select-input>
              <number-input
                label
                v-model.number="listing.price"
              >
                Price
              </number-input>
            </div>
          </template>
          <span slot="button">Add Content Catalog Listing</span>
        </array-input>

        <template v-if="registrationLink.catalog && registrationLink.catalog.length">
          <checkbox-input v-model="registrationLink.contentExpires">
            Catalog Content Access Should Expire
          </checkbox-input>
          <template v-if="registrationLink.contentExpires">
            <h6>
              Content Expiration Date (cannot access content after this time)
            </h6>
            <datetime
              v-model="registrationLink.contentExpirationDate"
              type="datetime"
              :id="`dtpick-ce`"
              use12-hour
              value-zone="UTC"
              zone="local"
            >
            </datetime>
            <number-input
              label
              v-model.number="registrationLink.contentExpirationDaysAfterPurchase"
            >
              Days Of Access After Purchase (if > 0, overrides Content Expiration Date)
            </number-input>
          </template>
        </template>

        <div
          v-if="creating"
        >Create registration link first before managing its coupons.</div>
        <collapsible-section
          v-else
          name="Coupons"
          :buttonStyle="{
            'font-size': '120%'
          }"
        >
          <div
            style="
              width: 100%;
              display: flex;
              flex-flow: row wrap;
              gap: 12px;
            "
          >
            <button
              class="btn"
              :disabled="generating"
              @click.prevent="() => { $refs.generateCouponsModal.show(); }"
            >Generate Coupons</button>
            <button
              class="btn"
              :disabled="deleting"
              @click.prevent="() => { $refs.deleteConfirmModal.show(); }"
            >
              Delete All Coupons
            </button>
            <button
              class="btn"
              :disabled="exporting"
              @click.prevent="() => downloadCSV(registrationLink)"
            >
              Download CSV
            </button>
          </div>
          <b-modal
            ref="generateCouponsModal"
            title="Generate Coupons"
            ok-title="Generate"
            @ok="() => onClickGenerateCoupons(registrationLink)"
          >
            <number-input label v-model="couponForGeneration.count">Number of Coupons</number-input>

            <text-input label v-model.trim="couponForGeneration.prefix">Code Prefix (optional)</text-input>
            <text-input label v-model.trim="couponForGeneration.description">Description (optional)</text-input>

            <checkbox-input v-model="couponForGeneration.isPercentage">Percentage</checkbox-input>
            <number-input
              v-if="couponForGeneration.isPercentage"
              label
              v-model="couponForGeneration.value"
            >
              Discount Percentage (0-1)
            </number-input>
            <number-input
              v-else
              label
              v-model="couponForGeneration.value"
            >
              Discount in USD
            </number-input>
            <number-input label v-model="couponForGeneration.maxUses">Max Uses</number-input>
          </b-modal>
          <b-modal
            ref="deleteConfirmModal"
            title="Confirm Multi-Delete"
            size="sm"
            ok-variant="danger"
            ok-title="Delete!"
            @ok="() => deleteAllCoupons(registrationLink)"
          >
            WARNING: you are about to delete all {{registrationLink.coupons.length}} coupons.
            <strong>THIS IS IRREVERSIBLE.</strong>
          </b-modal>
          <div
            style="
              width: 100%;
              display: flex;
              flex-flow: column nowrap;
            "
          >
            <div
              v-for="coupon in registrationLink.coupons"
              :key="`coupon${coupon.code}${coupon._id}`"
              style="
                border: 1px solid grey;
                border-radius: 3px;
                width: 100%;
                display: flex;
                flex-flow: row wrap;
                justify-content: space-between;
                align-items: center;
                padding: 0 12px;
              "
            >
              <div
                style="
                  flex: 1;
                "
              >{{ coupon.code }}</div>
              <div
                style="
                  flex: 1;
                "
              >Value: {{ coupon.isPercentage ? `${coupon.value * 100}%` : coupon.value }}</div>
              <div
                style="
                  flex: 1;
                  padding: 0 12px;
                "
              >Uses: {{ coupon.uses }}{{ coupon.maxUses ? ` / ${coupon.maxUses}` : '' }}</div>
              <router-link :to="`/blade/coupons/${coupon._id}/edit`" target="_blank" class="btn">Info</router-link>
            </div>
          </div>
        </collapsible-section>
      </collapsible-section>

      <array-input
        :items="registrationLink.games"
        :hide-button="registrationLink.games.length >= games.length"
      >
        <h5 slot="title">
          Assigned Gamulation(s) (overrides Active flag gamulation for this User)
        </h5>

        <select-input
          slot="item"
          slot-scope="{ item: game, index: i }"
          v-model="game._id"
          :options="
            unused(games).by('_id').in(registrationLink.games).by('_id').at(i)
          "
          use="_id"
          display="name"
          required
          autofocus
          :remove="
            () => {
              registrationLink.games.splice(i, 1);
            }
          "
          :to="
            (id) => {
              return `/blade/games/${id}/edit`;
            }
          "
        >
          Gamulation
        </select-input>
        <span slot="button">Add Gamulation</span>
      </array-input>

      <array-input
        :items="registrationLink.gameSets"
        :hide-button="
          registrationLink.gameSets &&
          registrationLink.gameSets.length >= gameSetList.length
        "
      >
        <h5 slot="title">
          Assigned Gamulation Set(s) (defines the sets of games that are visible/available to the registrationLink)
        </h5>

        <select-input
          slot="item"
          slot-scope="{ item: gameSet, index: i }"
          v-model="gameSet._id"
          :options="
            unused(gameSetList)
              .by('_id')
              .in(registrationLink.gameSets)
              .by('_id')
              .at(i)
          "
          use="_id"
          display="name"
          required
          autofocus
          :remove="
            () => {
              registrationLink.gameSets.splice(i, 1);
            }
          "
          :to="
            (id) => {
              return `/blade/gamesets/${id}/edit`;
            }
          "
        >
          Gamulation Set
        </select-input>
        <span slot="button">Add Gamulation Set</span>
      </array-input>

      <array-input
        :items="registrationLink.classes"
        :hide-button="registrationLink.classes.length >= classes.length"
      >
        <h5 slot="title">
          Assigned {{ classTypeNamePlural }} (overrides everything when set)
        </h5>

        <select-input
          slot="item"
          slot-scope="{ item: selectedClass, index: i }"
          class="col-sm-8"
          v-model="selectedClass._id"
          :options="
            unused(classes)
              .by('_id')
              .in(registrationLink.classes)
              .by('_id')
              .at(i)
          "
          use="_id"
          display="className"
          required
          autofocus
          :remove="
            () => {
              registrationLink.classes.splice(i, 1);
            }
          "
          :to="
            (id) => {
              return `/blade/classes/${id}/edit`;
            }
          "
        >
          {{ classTypeName }}
        </select-input>
        <span slot="button">Add {{ classTypeName }}</span>
      </array-input>

      <array-input
        :items="registrationLink.roles"
        :disabled="validator.roles.$invalid"
        :hide-button="
          !canAddRoles(registrationLink.roles) ||
          registrationLink.roles.length >=
            getUserRolesControlled(userRoles).length
        "
      >
        <h5 slot="title">Roles</h5>

        <select-input
          slot="item"
          slot-scope="{ item: role, index: i }"
          v-model="role._id"
          :disabled="
            !isOwnerOrBladeAdmin && `${me._id}` === `${registrationLink._id}`
          "
          :options="
            unused(getUserRolesControlled(userRoles))
              .by()
              .in(registrationLink.roles)
              .by('_id')
              .at(i)
          "
          required
          autofocus
          :remove="
            () => {
              registrationLink.roles.splice(i, 1);
            }
          "
        >
          Role
        </select-input>
        <span slot="button">Add User Role</span>
      </array-input>

      <collapsible-section
        name="Hub Label Overrides"
        :buttonStyle="{
          'font-size': '120%'
        }"
      >
        <text-input
          label
          no-count
          v-model.trim="registrationLink.hubClassesOverrideText"
        >
          Classes/Workgroups Override Text (if blank, will use the default)
        </text-input>
        <text-input
          label
          no-count
          v-model.trim="registrationLink.hubGamesOverrideText"
        >
          Gamulations Override Text (if blank, will use the default)
        </text-input>
      </collapsible-section>
    </template>
  </edit-page>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators';
import { mapActions, mapMutations, mapGetters } from 'vuex';
import { Datetime } from 'vue-datetime';
import FileSaver from 'file-saver';

import ArrayInput from '../../ui/inputs/ArrayInput';
import EditPage from '../../ui/pages/EditPage';

import AssetSelectInput from '../../ui/inputs/AssetSelectInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';
import NumberInput from '../../ui/inputs/NumberInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import SignInConfigEditor from './components/SignInConfigEditor';
import CollapsibleSection from '../../ui/inputs/CollapsibleSection';

import { unused } from '../../../utils/utils';
import { csvEscape } from '../../../utils/csv-util';

function computeEmailListFromString(list) {
  if (!list || list.length === 0) { return ['notifications@redmerproductions.com']; }

  const separated = list.split(',');
  if (separated.length === 0) return ['notifications@redmerproductions.com'];
  return separated;
}

export default {
  components: {
    ArrayInput,
    EditPage,
    AssetSelectInput,
    SelectInput,
    Datetime,
    TextInput,
    NumberInput,
    CheckboxInput,
    SignInConfigEditor,
    CollapsibleSection
  },
  data() {
    return {
      userRoles: [],
      gameSetList: [],
      classes: [],
      games: [],
      signInConfigs: [],

      arraysFlip: false,
      emailListFlip: false,

      utils: {
        unused,
      },

      registrationLink: {
        // metadata
        _id: '',
        link: '',
        name: '',
        signInConfig: '',

        gameSets: [],
        classes: [],
        games: [],
        roles: [{ _id: 'student' }],

        skipEmailVerification: false,
        customEulaUrl: '',
        enableRegistrationNotifications: false,
        registrationEmailList: '',

        uniqueIdSignin: false,
        uniqueIdLoginHeader: 'Login',
        uniqueIdLoginPlaceholder: 'Login Code',
        uniqueIdSigninAutoRegister: false,
        overrideAutogenFirstName: '',
        overrideAutogenLastName: '',
        qualtricsRedirectURL: '',
        qualtricsExitURL: '',
        disableCancelSurveyButton: false,
        cancelSurveyButtonText: '',

        ipBasedRateLimitCount: 0,
        ipBasedRateLimitWindowSeconds: 0,

        hubClassesOverrideText: '',
        hubGamesOverrideText: '',

        updated: new Date(),
      },
      validator: {
        name: { required },
        emailDomain: { maxLength: maxLength(300) },
        roles: {
          $each: {
            required,
          },
        },
        rateLimitUserCount: {
        },
        rateLimitWindowSeconds: {
        }
      },

      couponForGeneration: {
        count: 0,
        prefix: '',
        description: '',
        isPercentage: false,
        value: 0,
        maxUses: 0,
      },
      generating: false,
      deleting: false,
      exporting: false,
    };
  },
  computed: {
    ...mapGetters([
      'isCorpo',
      'me',
      'isInternalCorpo',
      'isOwnerOrBladeAdmin',
      'isNonBladeAdminContent',
      'activeSignInConfig',
      'classTypeNamePlural',
      'classTypeName',
      'canManageGames',
    ]),
    canEditCustomLink() {
      return !this.isCorpo ||
        ['admin-content'].some(role => this.me.roles.some(({ _id }) => _id === role));
    },
    studentEmployeeStringLowerPlural() {
      return this.isInternalCorpo
        ? 'students/employees'
        : this.isCorpo
          ? 'employees'
          : 'students';
    },
    creating() {
      return !this.$route.params.registrationLinkId;
    },
    emailRecipientList() {
      if (this.emailListFlip && !this.emailListFlip) return;

      let list = computeEmailListFromString(
        (this.$refs.editPage.model.registrationEmailList || '').trim()
      );
      list = list.map(a => `"${a}"`);
      return list.join(', ');
    },
  },
  methods: {
    ...mapMutations([
      'submitEditUpdate',
    ]),
    ...mapActions([
      'getUserTypes',
      'createRegistrationLink',
      'getRegistrationLink',
      'updateRegistrationLink',
      'deleteRegistrationLink',
      'getEnvironments',
      'getSignInConfigs',
      'getGameSets',
      'getClasses',
      'getGames',
      'flashDanger',
      'flashSuccess',
      'deleteCoupon',
      'generateCoupons',
    ]),
    rateLimitDisplayPeriod(registrationLink) {
      const seconds = registrationLink.ipBasedRateLimitWindowSeconds;
      if (seconds <= 60) {
        return `${seconds} seconds`;
      }
      return `${seconds / 60} minutes`;
    },
    getUrl(registrationLink) {
      if (registrationLink.link) {
        if (registrationLink.uniqueIdSignin) {
          return `${window.location.origin}/signin/link/${registrationLink.link}`;
        }
        return `${window.location.origin}/signup/link/${registrationLink.link}`;
      }
      if (registrationLink.isCustomLink) {
        return 'Custom registration link cannot be empty';
      }
      return 'No link has been generated';
    },
    getUserRolesControlled(userRoles) {
      if (this.isOwnerOrBladeAdmin) {
        return userRoles;
      }
      if (this.isNonBladeAdminContent) {
        return userRoles.filter(
          r => r !== 'owner' && r !== 'developer' && r !== 'blade'
        );
      }
      return userRoles.filter(
        r =>
          r !== 'owner' &&
          r !== 'developer' &&
          r !== 'blade' &&
          r !== 'admin-content'
      );
    },
    canAddRoles(roles) {
      return this.isOwnerOrBladeAdmin || roles.length < 1;
    },
    async deleteAllCoupons(link) {
      if (this.deleting || !(this.deleteCoupon)) return;
      this.deleting = true;

      let error = false;

      const removeAsync = async(item) => {
        try {
          await this.deleteCoupon(item);
          const message = `Coupon "${item.name}" deleted successfully`;
          console.log(message);
        } catch (_) {
          error = true;
          const message = `Error deleting coupon "${item.name}"`;
          console.error(message);
        }
      };

      await Promise.all(link.coupons.map(item => removeAsync(item)));

      this.deleting = false;
      if (error) {
        const message = 'Error while deleting. Check console for details.';
        this.flashDanger({ message });
      } else {
        const message = `${link.coupons.length} coupons deleted successfully`;
        this.flashSuccess({ message });
      }
      link.coupons = [];
      this.submitEditUpdate();
    },
    async onClickGenerateCoupons(link) {
      if (this.generating) return;
      this.generating = true;
      const expectedLength =
        parseInt((link.coupons && link.coupons.length) ? link.coupons.length : 0, 10)
        + parseInt(this.couponForGeneration.count, 10);
      this.flashSuccess({ message: 'Generating coupons...' });
      try {
        const coupons = await this.generateCoupons(this.couponForGeneration);
        if (link.coupons && link.coupons.length) {
          link.coupons.push(...coupons);
        } else {
          link.coupons = coupons;
        }
        console.log(`Expected: ${expectedLength}, Generated: ${link.coupons.length}`);
        if (expectedLength === link.coupons.length) {
          this.flashSuccess({ message: 'Coupons generated successfully' });
        } else {
          this.flashDanger({ message: `Error hit while generating coupons; ${coupons.length} coupons generated.` });
        }
        this.submitEditUpdate();
        this.generating = false;
      } catch (error) {
        console.error('Error generating coupons:', error);
        this.flashDanger({ message: 'Error hit while generating coupons; no coupons generated.' });
        this.generating = false;
      }
    },
    downloadCSV(link) {
      if (this.exporting) return;
      this.exporting = true;

      try {
        this.flashSuccess({ message: 'Data exporting to CSV...' });

        let csvString = '"code","uses","max uses","value"\n';

        if (link.coupons && link.coupons.length) {
          link.coupons.forEach((c) => {
            csvString += `${csvEscape(c.code)},${c.uses},${c.maxUses},${c.value}\n`;
          });
        }

        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
        FileSaver.saveAs(blob, `${link.name} Coupons.csv`.trim());

        this.$nextTick(() => { this.exporting = false; }); // lets you download again if you want
      } catch (_) {
        const message = 'Error downloading data, please try again';
        this.flashDanger({ message });
        this.exporting = false;
      }
    },
  },
  async mounted() {
    const classReq = this.getClasses();
    const signInConfigs = this.getSignInConfigs();
    const gameReq = this.getGames();
    const gameSetReq = this.getGameSets();
    const userRoleReq = this.getUserTypes();

    this.signInConfigs = await signInConfigs;
    this.classes = await classReq;
    this.games = await gameReq;
    this.gameSetList = await gameSetReq;
    this.userRoles = (await userRoleReq).userRoles;

    await this.$nextTick();
  },
};
</script>

<style lang="scss" scoped>
.form-image-background + span {
  display: block;
  margin: -10px 0 10px 10px;
}
.form-image-background {
  background-color: rgba(0, 0, 0, 0);
}
.role-override {
  border: 2px solid rgba(0, 0, 0, 0.4);
  padding: 10px;
  margin: 5px;
  margin-bottom: 10px;
}
.email-list-recipients {
  margin-top: 0px;
  margin-bottom: 10px;
  font-style: italic;
  color: rgba(0, 0, 0, 0.5);
}

.rate-limit-block {
  border: 2px solid rgba(0, 0, 0, 0.4);
  padding: 10px;
}

.rate-limit {
  padding: 10px;
  margin-top: 10px;
  margin-bottom: 10px;
  border: 2px solid rgba(0, 0, 0, 0.4);
  font-style: italic;
  color: rgba(0, 0, 0, 0.5);
}

.rate-limit-enabled {
  background-color: rgba(0, 255, 0, 0.1);
}

.rate-limit-disabled {
  background-color: rgba(255, 0, 0, 0.1);
}
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/RegistrationLinkEdit.vue